import { useState, useEffect, Suspense } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { useBrandLoader, useEffectiveBrand, BrandContext, useBrand } from './hooks/useBrand'
import { useAccountLoader, AccountContext, useAccount } from './hooks/useAccount'
import { syncSubscriptionWithWhop } from './lib/whopSync'
import { TiktokConnectProvider } from './hooks/useTiktokConnect'
import CheckoutActivation from './components/CheckoutActivation'
import ProductIdErrorBanner from './components/ProductIdErrorBanner'
import Login from './pages/Login'
import Sidebar from './components/Sidebar'
import PageLoading from './components/PageLoading'
import Home from './pages/Home'
import EmailVerificationGate from './components/EmailVerificationGate'
import { isHiringEnabled, isMessagesEnabled } from './lib/featureFlags'
import BrandSwitcher from './components/BrandSwitcher'
import AccountSwitcher from './components/AccountSwitcher'
// Onboarding gating lives in lib/access.ts — pure and unit-tested
// (src/lib/access.test.ts), because this rule decides whether a customer lands
// on the dashboard or the signup wizard. hasHiringAccess also lives there and
// is still tested, but nothing calls it since the free-platform pivot removed
// HiringGate's paywall; it is the re-gate hook if that decision is reversed.
import { needsOnboarding } from './lib/access'
// lazyRoute, not React.lazy: it survives the stale-chunk 404 that a deploy
// causes for tabs still running the previous index.html. See lib/lazyRoute.ts.
import { lazyRoute } from './lib/lazyRoute'

// Staging build is configured with branch "common_branch_refined"

// Route-level code splitting. Everything below is reached only after a
// navigation, so it loads on demand instead of riding in the initial bundle
// with the login screen. The app shell (Sidebar, gates, switchers), the login
// page and Home stay eager above — they are on the first paint of every visit.
// Suspense boundaries wrap both <Routes> below; PageLoading is the fallback.
const AcceptInvite = lazyRoute(() => import('./pages/AcceptInvite'))
const ActiveCampaigns = lazyRoute(() => import('./pages/ActiveCampaigns'))
const AddBrand = lazyRoute(() => import('./pages/AddBrand'))
const AgencyBilling = lazyRoute(() => import('./pages/AgencyBilling'))
const AgencyHome = lazyRoute(() => import('./pages/AgencyHome'))
const AgencySettings = lazyRoute(() => import('./pages/AgencySettings'))
const AgencyTeam = lazyRoute(() => import('./pages/AgencyTeam'))
const AnnualEntry = lazyRoute(() => import('./pages/AnnualEntry'))
const AppLinkRedirect = lazyRoute(() => import('./pages/AppLinkRedirect'))
const Applications = lazyRoute(() => import('./pages/Applications'))
const AuditLog = lazyRoute(() => import('./pages/AuditLog'))
const AuthCallback = lazyRoute(() => import('./pages/AuthCallback'))
const BMCMax = lazyRoute(() => import('./pages/BMCMax'))
const CampaignDetail = lazyRoute(() => import('./pages/CampaignDetail'))
const CampaignNew = lazyRoute(() => import('./pages/CampaignNew'))
const CampaignPerformance = lazyRoute(() => import('./pages/CampaignPerformance'))
const CampaignView = lazyRoute(() => import('./pages/CampaignView'))
const Campaigns = lazyRoute(() => import('./pages/Campaigns'))
const CreatorRoster = lazyRoute(() => import('./pages/CreatorRoster'))
const CreatorsLayout = lazyRoute(() => import('./pages/CreatorsLayout'))
const DebugSentry = lazyRoute(() => import('./pages/DebugSentry'))
const DesignSystemPreview = lazyRoute(() => import('./pages/DesignSystemPreview'))
const Discover = lazyRoute(() => import('./pages/Discover'))
const HireCreators = lazyRoute(() => import('./pages/HireCreators'))
const HiredCreators = lazyRoute(() => import('./pages/HiredCreators'))
const HiresRedirect = lazyRoute(() => import('./pages/HiresRedirect'))
const HowItWorks = lazyRoute(() => import('./pages/HowItWorks'))
const InvitedCreators = lazyRoute(() => import('./pages/InvitedCreators'))
const LaunchCall = lazyRoute(() => import('./pages/LaunchCall'))
const Messages = lazyRoute(() => import('./pages/Messages'))
const MessagesDashboardPreview = lazyRoute(() => import('./pages/MessagesDashboardPreview'))
const MessagesPreview = lazyRoute(() => import('./pages/MessagesPreview'))
const MobileAppRedirect = lazyRoute(() => import('./pages/MobileAppRedirect'))
const MyVideos = lazyRoute(() => import('./pages/MyVideos'))
const Onboarding = lazyRoute(() => import('./pages/Onboarding'))
const Playbook = lazyRoute(() => import('./pages/Playbook'))
const PrivacyPolicy = lazyRoute(() => import('./pages/PrivacyPolicy'))
const PublicAcceptOffer = lazyRoute(() => import('./pages/PublicAcceptOffer'))
const PublicApply = lazyRoute(() => import('./pages/PublicApply'))
const PublicDeclineOffer = lazyRoute(() => import('./pages/PublicDeclineOffer'))
const PublicOfferStatus = lazyRoute(() => import('./pages/PublicOfferStatus'))
const PublicSetRates = lazyRoute(() => import('./pages/PublicSetRates'))
const Reconciliation = lazyRoute(() => import('./pages/Reconciliation'))
const RoiAnalytics = lazyRoute(() => import('./pages/RoiAnalytics'))
const SampleRequests = lazyRoute(() => import('./pages/SampleRequests'))
const Settings = lazyRoute(() => import('./pages/Settings'))
const Support = lazyRoute(() => import('./pages/Support'))
const Team = lazyRoute(() => import('./pages/Team'))
const TermsConditions = lazyRoute(() => import('./pages/TermsConditions'))
const UpgradeModal = lazyRoute(() => import('./components/UpgradeModal'))
const ConfirmHirePreview = lazyRoute(() => import('./pages/FeePreview').then(m => ({ default: m.ConfirmHirePreview })))
const SendOfferPreview = lazyRoute(() => import('./pages/FeePreview').then(m => ({ default: m.SendOfferPreview })))


// Two-layer gate for the Hiring section:
//   1. Phase-1 launch flag (isHiringEnabled). When off, hiring is hidden
//      entirely — kicks the user back to Home. Devs override via DevTools.
//   2. Subscription gate. Brands without an actual Whop subscription get
//      redirected to /pricing. BMC Max is NOT wrapped — stays a
//      discoverable upsell (it's the managed-service offering).
//
// IMPORTANT: we check whop_subscription_id, NOT plan_status alone. The
// brand_configs.plan_status column defaults to 'active' at row creation
// (see migration 20260417213638_create_brand_hiring_v1.sql), so checking
// only plan_status would let every free user through. The actual signal
// for "this brand has paid access" is whop_subscription_id being set —
// matching SendOfferModal's `canChargeOffers` check.
function HiringGate({ children }: { children: React.ReactNode }) {
  // Free-platform pivot (2026-08-24): hiring is open to every brand — the
  // subscription check that used to bounce free tiers to /pricing is gone.
  // The component shell (and its 12 route wrappings) is kept so a future
  // gate — or an emergency re-gate — is a one-function change. Monetization
  // now happens per-hire (card charged on confirmed hire), on the managed
  // service ($500/mo, sold on onboarding calls), and on backend deals.
  // First-campaign launch gating lives in CampaignNew, not here.
  //
  // The merge from development restored the old paywall here (that branch is
  // production, which predates the pivot). It compiled cleanly and would have
  // silently re-blocked every free brand from all 12 hiring routes.
  if (!isHiringEnabled()) return <Navigate to="/" replace />
  return <>{children}</>
}

// Full-page pricing intercept. Reached when a brand without an active
// subscription tries to hit any HiringGate-wrapped route. Renders the
// UpgradeModal in `intercept` mode so the copy is hiring-specific and the
// close (X) is hidden — they must start a trial or navigate away via the
// sidebar.
//
// Before showing the paywall to an unsubscribed brand, asks the worker to
// pull subscription state straight from Whop (/api/whop/sync). This is the
// safety net for checkout returns that DON'T carry ?from=checkout (Whop
// plan-level settings can override the redirect_url we register — seen with
// the annual plan): the freshly-paid user clicks a hiring route, HiringGate
// bounces them here, and instead of a paywall claiming they never paid, the
// sync finds the membership, the context refreshes, and they move on to
// /campaigns.
function PricingPage() {
  const { brand, reloadSilent } = useBrand()
  const [phase, setPhase] = useState<'checking' | 'paywall' | 'activated'>('checking')

  useEffect(() => {
    if (!brand?.id) return
    // Already-subscribed brands skip the sync — the modal renders their
    // current-plan state (what /annual relies on for paid visitors).
    if (brand.whop_subscription_id) {
      setPhase('paywall')
      return
    }
    let cancelled = false
    syncSubscriptionWithWhop(brand.id).then(async subscribed => {
      if (cancelled) return
      if (subscribed) {
        await reloadSilent()
        if (!cancelled) setPhase('activated')
      } else {
        setPhase('paywall')
      }
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id])

  if (!brand) return null
  if (phase === 'activated') return <Navigate to="/campaigns" replace />
  if (phase === 'checking') {
    return (
      <div style={{
        minHeight: '60vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 12,
      }}>
        <div className="loading-dots" style={{ fontSize: 18, color: 'var(--text-tertiary)' }}>
          <span>.</span><span>.</span><span>.</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
          Checking subscription status
        </div>
      </div>
    )
  }
  return (
    <UpgradeModal
      brandId={brand.id}
      currentTier={brand.plan_tier ?? 'free'}
      onClose={() => { /* no-op in intercept mode */ }}
      intercept
    />
  )
}

/**
 * Refresh the brand row whenever the tab regains focus.
 *
 * Every "book your onboarding call" link outside the two inline embeds opens
 * Calendly in a NEW TAB — the checklist, the Listings banner, /how-it-works.
 * The dashboard gets no signal from those at all: the Calendly webhook stamps
 * brand_configs.launch_call_booked_at server-side, but nothing in the open tab
 * ever refetches, so the brand comes back to a checklist still telling them to
 * book the call they just booked, until they happen to hard-reload.
 *
 * Coming back to the tab is the one reliable moment we know something may have
 * changed elsewhere. This is the same pattern the old sidebar PlanBadge used
 * for subscription state — that component is no longer rendered, so the
 * behaviour had quietly disappeared with it.
 */
function BrandFocusRefresh({ reloadSilent }: { reloadSilent: () => Promise<void> }) {
  useEffect(() => {
    const onFocus = () => { void reloadSilent() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [reloadSilent])
  return null
}

function DefaultRedirect() {
  // Agencies land on the Agency Home (lobby) on login; once they've entered a
  // brand, "/" is that brand's dashboard. Solo brands always land on Home.
  const { isAgency, inBrand, loading } = useAccount()
  // Wait for the account to resolve so we don't flash Home before redirecting
  // an agency to its lobby.
  if (loading) return null
  if (isAgency && !inBrand) return <Navigate to="/agency" replace />
  return <Home />
}

// Once an agency cancels its $997 subscription (plan_status='canceled'), it can
// no longer manage brands: if the user is currently INSIDE a brand, bounce them
// back to the agency lobby. Add-brand and brand-entry are also blocked at their
// own entry points (AddBrand redirect + AgencyHome disabled cards); this is the
// catch-all for "was already inside a brand when the cancellation landed".
/**
 * Bounce out of a brand whose access has gone away.
 *
 * The entry check in enterBrand covers the click; this covers the other half —
 * already being inside a brand when an agency admin revokes it. useAccount
 * revalidates when the tab regains focus, so the accessible list refreshes on
 * its own; when the active brand isn't in it any more, sitting on a dashboard
 * where every request 403s is worse than being told.
 *
 * Deliberately narrow. It fires only for an ACCOUNT workspace (a solo brand or a
 * standalone-brand workspace resolves its brands differently, and `brands` is
 * not the authority there) and only once the load has settled, so a mid-fetch
 * empty list can never evict anyone.
 */
function BrandAccessGuard() {
  const { activeWorkspace, brands, activeBrandId, inBrand, loading, noteAccessRevoked, exitToAgency } = useAccount()
  const navigate = useNavigate()
  const lost =
    !loading &&
    inBrand &&
    activeWorkspace?.kind === 'account' &&
    !!activeBrandId &&
    !brands.some(b => b.id === activeBrandId)

  useEffect(() => {
    if (!lost || !activeBrandId) return
    noteAccessRevoked({
      id: activeBrandId,
      name: brands.find(b => b.id === activeBrandId)?.brand_name ?? 'That brand',
    })
    exitToAgency()
    navigate('/agency', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lost, activeBrandId])
  return null
}

function AgencyAccessGuard() {
  const { isAgency, inBrand, account, exitToAgency } = useAccount()
  const navigate = useNavigate()
  // Lock ONLY after the paid period has actually ended — i.e. cancelled AND the
  // sub is gone (the period-end webhook clears whop_subscription_id). During the
  // cancel→period-end window the sub is still set, so the agency keeps full
  // control (this is the "keep access until the period ends" behavior).
  const locked = isAgency && inBrand && account?.plan_status === 'canceled' && !account?.whop_subscription_id
  useEffect(() => {
    if (locked) {
      exitToAgency()
      navigate('/agency', { replace: true })
    }
  }, [locked, exitToAgency, navigate])
  return null
}

function AuthenticatedApp() {
  const { user, signOut } = useAuth()
  const baseBrandState = useBrandLoader(user?.id)
  const accountState = useAccountLoader(baseBrandState.brand, user?.id, baseBrandState.loading)
  // Re-scope the brand context to the active brand chosen in the agency switcher
  // so the whole dashboard reads AND WRITES that brand's data (not the anchor
  // brand's). userId is needed for the active brand's role lookup.
  const brandState = useEffectiveBrand(baseBrandState, accountState, user?.id)

  // An agency can have ZERO brands, so the account loader (not the brand loader)
  // is the source of truth for "is this user set up?" — wait for both.
  if (brandState.loading || accountState.loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '12px',
      }}>
        <img src="/logo-dark.png" alt="Brands Meet Creators" style={{ height: '56px', width: 'auto' }} />
        <div className="brand-loader-bar" />
      </div>
    )
  }

  // Email verification is enforced one level up at <App />, so by the time we
  // reach AuthenticatedApp the user is already verified.
  //
  // Sidebar is rendered at this level (not inside Dashboard or Onboarding) so
  // its instance is preserved across the Onboarding-submitting → dashboard
  // transition. Without this, swapping component trees on brand state update
  // remounts Sidebar (re-fetching plan, resetting state, etc.) and produces a
  // visible glitch right when the route changes after onboarding.
  //
  // The Onboarding form state covers the sidebar with a fixed-position overlay
  // (see Onboarding's `styles.page`) — once submitting=true it drops the
  // overlay so the loading screen sits next to the already-mounted sidebar.
  // Returning from a Whop checkout (?from=checkout) is NOT onboarding even if
  // onboarding_completed is still false — we no longer flip it before the
  // redirect (so an abandoned checkout keeps the user in onboarding). Let the
  // routes render so CheckoutActivation can confirm the subscription and then
  // mark onboarding complete. Without this gate the brand-still-false row would
  // re-render Onboarding over the activation screen.
  const fromCheckout = new URLSearchParams(window.location.search).get('from') === 'checkout'

  // A brand READ that broke is not a brand-new user. useBrandLoader reports
  // 'no_brand_access' only when the membership query succeeded and came back
  // empty (a genuine new signup); 'brand_not_found' and 'Failed to load brand'
  // mean the read itself failed. Both used to fall through to `brand === null`
  // → the signup wizard, so a transient error showed a paying customer step 1 of
  // onboarding — with a real risk of them creating a duplicate brand from it.
  if (baseBrandState.error === 'brand_not_found' || baseBrandState.error === 'Failed to load brand') {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 16, padding: 24, textAlign: 'center',
      }}>
        <img src="/logo-dark.png" alt="Brands Meet Creators" style={{ height: 56, width: 'auto' }} />
        <div style={{ fontSize: 15, color: 'var(--text-secondary)', maxWidth: 420 }}>
          We couldn't load your account. This is usually temporary — your data is safe.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => { void baseBrandState.reload() }} style={{
            padding: '10px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
            background: '#FB3AAE', color: '#fff', fontSize: 14, fontWeight: 600,
          }}>Try again</button>
          <button onClick={signOut} style={{
            padding: '10px 18px', borderRadius: 999, cursor: 'pointer',
            border: '1px solid var(--border-primary)', background: 'transparent',
            color: 'var(--text-secondary)', fontSize: 14,
          }}>Sign out</button>
        </div>
      </div>
    )
  }

  // "Set up?" is account-aware now that an agency can have ZERO brands:
  //   - agency account → its own onboarding_completed flag (brand-independent)
  //   - brand context  → the brand's onboarding_completed
  //   - neither        → brand-new user → onboarding (the step-1 Brand/Agency toggle)
  // The ANCHOR brand is passed separately from the effective one: useEffectiveBrand
  // masks brandState.brand to null for an agency sitting at the lobby, and the
  // agency branch needs to see that this user already owns a real, set-up brand.
  const isOnboarding = !fromCheckout && needsOnboarding({
    account: accountState.account,
    brand: brandState.brand,
    anchorBrand: baseBrandState.brand,
  })
  return (
    <BrandContext.Provider value={brandState}>
      <AccountContext.Provider value={accountState}>
      <BrandFocusRefresh reloadSilent={brandState.reloadSilent} />
      <AgencyAccessGuard />
      <BrandAccessGuard />
      <TiktokConnectProvider>
      <div style={{ display: 'flex', minHeight: '100dvh' }}>
        <Sidebar signOut={signOut} hideMobileToggle={!!isOnboarding} />
        <main className="app-main" style={{ flex: 1, marginLeft: 'var(--sidebar-width)', minHeight: '100dvh', padding: '40px 48px calc(20px + env(safe-area-inset-bottom))' }}>
          {isOnboarding ? (
            <Suspense fallback={<PageLoading />}>
              <Onboarding signOut={signOut} userId={user?.id} />
            </Suspense>
          ) : (
            <>
            {/* Global switcher bar — slim, top-LEFT, on every page. Left side
                keeps it clear of page filters (which sit top-right) and out of
                the sidebar.
                EXACTLY ONE switcher renders here, never both. They are competing
                models for the same job, and showing them together produced two
                adjacent controls reading "BRAND / <same name>":
                  - AccountSwitcher (preferred): WORKSPACES — every account, plus
                    standalone brands. Brands inside an agency are reached through
                    that agency, which keeps the account context (billing, team,
                    activity) consistent with the brand on screen.
                  - BrandSwitcher (fallback): a flat list of every brand the user
                    can reach. Correct only when there are no workspaces to choose
                    between — e.g. someone invited to several SOLO brands' teams,
                    who has no account membership at all. */}
            {(() => {
              // Each control's own visibility, mirrored here so the bordered row
              // itself doesn't render empty when both are hidden.
              const workspaceCount = accountState.accounts.length + accountState.standaloneBrands.length
              // Mirrors AccountSwitcher's own rule: more than one workspace, and
              // not inside an AGENCY's brand (where the lobby is the place to
              // switch). A brand workspace has no lobby, so it stays visible.
              const showAccount =
                workspaceCount > 1 && !(accountState.isAgency && accountState.inBrand)
              // `!showAccount` is what makes them mutually exclusive. A flat brand
              // list alongside the workspace switcher would also bypass the
              // workspace model: picking an agency's brand from it moves the brand
              // without moving the account, leaving the two out of sync.
              const showBrand =
                !showAccount && !accountState.isAgency && brandState.brands.length > 1
              if (!showAccount && !showBrand) return null
              return (
                <div style={{
                  display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10,
                  marginBottom: 24, paddingBottom: 16,
                  borderBottom: '1px solid var(--border-primary)',
                }}>
                  {showAccount && <AccountSwitcher />}
                  {showBrand && <BrandSwitcher />}
                </div>
              )
            })()}
            {/* Global "invalid product ID" banner — mounted once here so it's
                visible on every page, not just the campaign editor. Reflects
                the worker's background product-detail cache refresh. */}
            <ProductIdErrorBanner />
            {/* CheckoutActivation holds the routes back (with an "activating
                your subscription" screen) when the user returns from Whop
                checkout before the webhook has written brand_configs — without
                it, HiringGate sees the stale free-tier row and bounces the
                freshly-paid user to /pricing. */}
            <CheckoutActivation>
            {/* One boundary for every lazily-loaded route below. PageLoading is
                the app's canonical in-page loader, so a route chunk arriving a
                beat late looks like any other page load rather than a blank
                screen. The shell (sidebar, switchers, banners) stays painted. */}
            <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route index element={<DefaultRedirect />} />
              {/* Agency Home — the brand roster for agency accounts (spec §7).
                  Always reachable; DefaultRedirect sends agencies here. */}
              <Route path="agency" element={<AgencyHome />} />
              {/* Agency-level Billing + Settings — the account's OWN subscription
                  and details (not a brand's). Reached from the agency-lobby
                  sidebar tabs; both pages are owner-only + redirect otherwise. */}
              <Route path="agency/billing" element={<AgencyBilling />} />
              <Route path="agency/settings" element={<AgencySettings />} />
              {/* Agency-level Team — account members + which brands each can
                  work in. Owner/admin only (the page redirects others); the
                  worker re-checks account_members on every mutation. */}
              <Route path="agency/team" element={<AgencyTeam />} />
              {/* Agency-level Activity — the agency's OWN trail (team, brand
                  access, roster). Agency actions are recorded here and NOT in
                  each brand's log. Owner/admin only; the page redirects. */}
              <Route path="agency/activity" element={<AuditLog scope="agency" />} />
              {/* Add a brand under the agency account (spec §5). Renders an
                  upgrade prompt for solo accounts. */}
              <Route path="agency/add-brand" element={<AddBrand />} />
              {/* Hiring routes — gated by isHiringEnabled(). When the flag is off
                  (Phase 1 default in prod) anyone hitting these URLs directly
                  (bookmark, deep link, etc.) is redirected back to Home, matching
                  the sidebar's "Coming soon" state. Devs flip via DevTools. */}
              <Route path="hire" element={<HiringGate><HireCreators /></HiringGate>} />
              <Route path="hire/:campaignId" element={<HiringGate><CampaignDetail /></HiringGate>} />
              <Route path="active-campaigns" element={<HiringGate><ActiveCampaigns /></HiringGate>} />
              {/* /roster — nested layout with shared header + tab strip.
                  Index = Browse (CreatorRoster body); /roster/invited =
                  InvitedCreators body; /roster/applications = Applications
                  body. Layout stays mounted across tab swaps so navigating
                  between tabs no longer remounts the page (was a noticeable
                  full-page reload before). */}
              <Route path="roster" element={<HiringGate><CreatorsLayout /></HiringGate>}>
                <Route index element={<CreatorRoster />} />
                <Route path="invited" element={<InvitedCreators />} />
                <Route path="applications" element={<Applications />} />
              </Route>
              <Route path="videos" element={<HiringGate><MyVideos /></HiringGate>} />
              {/* BMC Max stays accessible in Phase 1 — it's a high-value
                  done-for-you upsell that we explicitly want users to discover
                  even before hiring features ship. NOT wrapped in HiringGate
                  so unsubscribed brands can also reach it. */}
              <Route path="bmc-max" element={<BMCMax />} />
              {/* Pricing — full-page subscription intercept. HiringGate
                  redirects unsubscribed brands here when they try to hit a
                  hiring route. NOT wrapped in HiringGate (would loop). */}
              <Route path="pricing" element={<PricingPage />} />
              {/* Free-pivot education + fee transparency page — never gated. */}
              <Route path="how-it-works" element={<HowItWorks />} />
              {/* Settings — placeholder, opened from the brand menu in the
                  sidebar bottom. Read-only brand profile for now. */}
              <Route path="settings" element={<Settings />} />
              {/* Team — multi-user management. Admin-only actions are enforced
                  in the worker; the page itself shows a read-only roster to
                  non-admins. Not behind HiringGate (account-level, not hiring). */}
              <Route path="team" element={<Team />} />
              {/* Audit / activity log — admin-only (the page itself redirects
                  non-admins). Linked from the brand menu. */}
              <Route path="activity" element={<AuditLog />} />
              {/* Support — ticket list + chat with the BMC team, replacing the
                  old SupportModal. Not HiringGate'd: every brand, subscribed
                  or not, can reach support. Linked from the sidebar's "Need
                  Help?" nav item. */}
              <Route path="support" element={<Support />} />
              <Route path="campaigns" element={<HiringGate><Campaigns /></HiringGate>} />
              {/* /campaigns/new — multi-step Launch Campaign wizard.
                  Replaces the previous in-modal create flow. Lives behind
                  HiringGate same as /campaigns. */}
              <Route path="campaigns/new" element={<HiringGate><CampaignNew /></HiringGate>} />
              {/* Post-publish: first job saved -> book the onboarding call */}
              <Route path="campaigns/launch-call" element={<HiringGate><LaunchCall /></HiringGate>} />
              {/* Back-compat: bookmarks / external links / old emails that
                  point at /applications get redirected into the nested
                  /roster/applications tab. */}
              <Route path="applications" element={<Navigate to="/roster/applications" replace />} />
              {/* Back-compat only: /hires was retired in the 2026-05-02 IA
                  redesign. HiresRedirect forwards old email CTAs and bookmarks
                  to the matching Invited Creators tab (or Hired Creators). */}
              <Route path="hires" element={<HiringGate><HiresRedirect /></HiringGate>} />
              <Route path="hired-creators" element={<HiringGate><HiredCreators /></HiringGate>} />
              <Route path="samples" element={<HiringGate><SampleRequests /></HiringGate>} />
              {/* Messages — brand ↔ creator DMs, unlocked by a confirmed hire.
                  Gated like the rest of Hiring, PLUS the VITE_ENABLE_MESSAGES
                  flag: when messages are off, direct navigation to /messages
                  redirects home so the feature is fully hidden. */}
              <Route path="messages" element={isMessagesEnabled() ? <HiringGate><Messages /></HiringGate> : <Navigate to="/" replace />} />
              {/* NOT HiringGate'd — deliberately. Billing is the brand's own
                  money, not a hiring feature: the page already renders the
                  free-tier case ("Free (no subscription)" + upgrade CTA), and
                  the sidebar already offers Billing to every brand ADMIN
                  regardless of tier — so gating the route only meant a free
                  admin clicking their own Billing link got bounced to
                  /pricing. It also made wallet credit unreachable for free
                  brands, who can hold it (an admin grant) and buy it (a credit
                  top-up). Every HIRING route above stays gated; this grants no
                  hiring access. */}
              <Route path="billing" element={<Reconciliation />} />
              {/* Wallet credit moved INTO Billing (client call 2026-08-06), so
                  /wallet is now a redirect rather than a 404 — it was linked from
                  the sidebar and may be bookmarked. */}
              <Route path="wallet" element={<Navigate to="/billing" replace />} />
              <Route path="campaigns/:campaignId" element={<HiringGate><CampaignPerformance /></HiringGate>} />
              {/* Read-only campaign detail: wizard fields locked + creator-app
                  preview. Where the /campaigns list cards land now (non-draft). */}
              <Route path="campaigns/:campaignId/view" element={<HiringGate><CampaignView /></HiringGate>} />
              {/* ROI & Analytics — brand-wide creator performance. NOT behind
                  HiringGate: every brand can open it regardless of
                  subscription; brands with no hires get an empty state. */}
              <Route path="analytics" element={<RoiAnalytics />} />
              {/* Intelligence (always available) */}
              <Route path="discover" element={<Discover />} />
              <Route path="playbook" element={<Playbook />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            </CheckoutActivation>
            </>
          )}
        </main>
      </div>
      </TiktokConnectProvider>
      </AccountContext.Provider>
    </BrandContext.Provider>
  )
}

export default function App() {
  const { user, loading, signOut, isRecovery, oauthError } = useAuth()

  if (loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '12px',
      }}>
        <img src="/logo-dark.png" alt="Brands Meet Creators" style={{ height: '56px', width: 'auto' }} />
        <div className="brand-loader-bar" />
      </div>
    )
  }

  // Public routes are lazy too — a logged-out visitor on /privacy should not
  // download the authenticated dashboard, and vice versa.
  return (
    <Suspense fallback={<PageLoading minHeight="100vh" />}>
    <Routes>
      {/* Public routes — no auth required */}
      {/* Admin impersonation / magic-link landing — exchanges the one-time
          token_hash for a session (see AuthCallback.tsx). Must stay public so
          it runs BEFORE the auth gate (the whole point is to log the user in). */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/design" element={<DesignSystemPreview />} />
      {/* Public messaging UI preview — mock data, no auth/DB. Local QA only. */}
      <Route path="/preview/messages" element={<MessagesPreview />} />
      <Route path="/preview/dashboard" element={<MessagesDashboardPreview />} />
      <Route path="/accept/:token" element={<MobileAppRedirect variant="accept" />} />
      {/* Campaign blast invite → deep-links into the in-app job/apply screen */}
      <Route path="/invite/:token" element={<MobileAppRedirect variant="invite" />} />
      {/* Generic "come back to Jobs" links (e.g. invite-withdrawn email) →
          deep-links into the Jobs tab's Browse section, not a specific campaign */}
      <Route path="/browse-jobs/:token" element={<MobileAppRedirect variant="browse" />} />
      {/* Generic app-page deep link — CTA links generated by bmc-admin's
          Deep Link Generator (e.g. newsletters). :id is only present for
          pages that need one (campaign/job/offer detail). */}
      <Route path="/l/:pageKey" element={<AppLinkRedirect />} />
      <Route path="/l/:pageKey/:id" element={<AppLinkRedirect />} />
      <Route path="/decline/:token" element={<PublicDeclineOffer />} />
      {/* Team invite accept — public (the token is the credential). New users
          set a password + join; existing users one-click accept. */}
      <Route path="/accept-invite/:token" element={<AcceptInvite authUser={user} oauthError={oauthError} />} />
      {/* Agency team invite accept — same page, account-level token. Carries
          access to one or more of the agency's brands. */}
      <Route path="/accept-agency-invite/:token" element={<AcceptInvite scope="agency" authUser={user} oauthError={oauthError} />} />
      <Route path="/offer-status" element={<PublicOfferStatus />} />
      <Route path="/set-rates/:token" element={<PublicSetRates />} />
      {/* Deprecated web apply flow — superseded by the in-app apply screen.
          Route + PublicApply left dormant; no longer linked from emails. */}
      <Route path="/apply/:token" element={<PublicApply />} />

      {/* /annual — phone-closed annual-plan funnel shortcut. Public so sales
          can DM the link to customers fresh from a demo call; AnnualEntry
          itself handles the auth/state routing. See AnnualEntry.tsx. */}
      <Route path="/annual" element={<AnnualEntry />} />

      {/* Sentry test route — dev/staging only, the component itself renders
          a 404 message when VITE_ENV === 'production'. */}
      <Route path="/debug-sentry" element={<DebugSentry />} />

      {/* Design preview — Hired Creators with mock fixtures (no auth, no DB).
          Covers every visual state incl. delivered-video expansions, for
          debugging the UI on accounts with no video data. */}
      <Route path="/preview/hired-creators" element={<HiredCreators mock />} />

      {/* Design previews — the two money-moment modals with mock props, for
          eyeballing the service-fee whisper in the real components. */}
      <Route path="/preview/send-offer" element={<SendOfferPreview />} />
      <Route path="/preview/confirm-hire" element={<ConfirmHirePreview />} />

      {/* Auth-gated routes */}
      <Route
        path="*"
        element={
          !user || isRecovery ? (
            <Login oauthError={oauthError} />
          ) : (
            <EmailVerificationGate user={user} signOut={signOut}>
              <AuthenticatedApp />
            </EmailVerificationGate>
          )
        }
      />
    </Routes>
    </Suspense>
  )
}
